import { useRef, type ChangeEvent, type ReactNode } from "react";
import { App_Url } from "@/src_new/whiteLabel";
import { AppImage } from "./AppImage";

type Props = {
  imageSrc?: string;
  imageAlt?: string;
  badgeIconSrc?: string;
  badgeIconAlt?: string;
  badgeText?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  supportedDocuments?: ReactNode;
  uploadButtonText?: string;
  scanningButtonText?: string;
  scanningLabel?: ReactNode;
  showUploadButton?: boolean;
  accept?: string;
  className?: string;
  isScanning?: boolean;
  progress?: number;
  onCancel?: () => void;
  onDocumentUpload?: (e: ChangeEvent<HTMLInputElement>) => void;
};

const OcrUploadComponent = ({
  imageSrc = App_Url.quoteFlow.UploadIllustration,
  imageAlt = "Travel illustration",
  badgeIconSrc = App_Url.quoteFlow.SaveTimeIcon,
  badgeIconAlt = "save time",
  badgeText = "Save time (Recommended)",
  title,
  description = "Upload one of your Vehicle documents and we'll auto-fill most details for you.",
  supportedDocuments = (
    <>
      [<span>Air Tickets</span> / <span>Travel itinerary </span> / <span>Holiday Plan</span>]
    </>
  ),
  uploadButtonText = "Upload Your Document",
  scanningButtonText = "Scanning...",
  scanningLabel = "Scanning document…",
  showUploadButton = true,
  accept = ".pdf,.png,.jpg,.jpeg",
  className = "",
  isScanning = false,
  progress = 0,
  onCancel,
  onDocumentUpload,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (!isScanning) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div
      className={`flex w-full flex-col items-center justify-center pt-20 xs:pt-10 ${className}`.trim()}
    >
      <AppImage src={imageSrc} alt={imageAlt} className="w-full max-w-[160px] object-contain" />

      <div className="flex flex-col items-center text-center">
        {badgeText ? (
          <h2 className="text-sm mt-4 mb-4 flex items-center justify-center">
            {badgeIconSrc ? (
              <AppImage
                src={badgeIconSrc}
                alt={badgeIconAlt}
                className="mr-2 w-full max-w-[30px] object-contain"
              />
            ) : null}
            {badgeText}
          </h2>
        ) : null}

        {title ? <h3 className="mb-2 text-lg font-semibold text-theme-text">{title}</h3> : null}

        {description ? <p className="mb-2 text-sm text-center">{description}</p> : null}

        {supportedDocuments ? (
          <p className="options_doc mt-2 mb-4 text-sm text-center">{supportedDocuments}</p>
        ) : null}

        {showUploadButton ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={accept}
              onChange={onDocumentUpload}
            />

            <button
              type="button"
              onClick={handleButtonClick}
              disabled={isScanning}
              aria-live="polite"
              className={`btn btn-upldoc inline-flex items-center justify-center gap-2 ${
                isScanning ? "cursor-not-allowed opacity-80" : ""
              }`}
            >
              <div className="flex w-5 flex-shrink-0 items-center justify-center">
                {isScanning ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </div>

              <span className="font-medium">
                {isScanning ? scanningButtonText : uploadButtonText}
              </span>
            </button>
          </>
        ) : null}

        {isScanning && (
          <div className="w-full max-w-xs mt-4">
            <div className="h-2 bg-theme-border/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-theme-accent-strong transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="flex justify-between text-xs mt-1 text-theme-text-muted">
              <span>{scanningLabel}</span>
              <span>{progress}%</span>
            </div>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="mt-2 text-sm text-theme-danger hover:underline"
              >
                Cancel upload
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OcrUploadComponent;
